import React from 'react';
import styled, { css } from 'styled-components';

import { convertThumbnailToPictureImage } from '@tager/web-modules';

import { colors } from '@/constants/theme';
import { ProductCardFormType } from '@/components/ProductCard/ProductCardForm';
import Picture from '@/components/Picture';
import { normalizePrice } from '@/utils/common';
import {useSettingItem} from "@/hooks";

type Props = {
  product: Omit<ProductCardFormType, 'stock'>;
};

const getDiscountPLus = (value: number, discount: number) => {
  return Number(Math.ceil(value - value * (discount / 100)).toFixed(2));
};

function OrderCard({ product }: Props) {
  const {
    image,
    name,
    price,
    discountPrice,
    hasDiscount,
    discount,
    optionName,
    optionValue,
  } = product;

  const currency = useSettingItem('CURRENCY');

  return (
    <Component>
      <PictureWrapper>
        <StyledPicture {...convertThumbnailToPictureImage(image)} />
      </PictureWrapper>
      <DescriptionWrapper>
        <Name>{name}</Name>
        <PriceWrapper>
          {price && discountPrice && !hasDiscount ? (
            <>
              <Price hasDiscount>{normalizePrice(price)} {currency}</Price>
              <Price>{normalizePrice(discountPrice)} {currency}</Price>
            </>
          ) : price && discountPrice && hasDiscount ? (
            <>
              <Price hasDiscount>{normalizePrice(price)} {currency}</Price>
              <Price>
                {normalizePrice(discountPrice)} {currency}
              </Price>
            </>
          ) : price && discount ? (
            <>
              <Price>{normalizePrice(price)} {currency}</Price>
            </>
          ) : price && !discount ? (
            <Price>{normalizePrice(price)} {currency}</Price>
          ) : null}
        </PriceWrapper>
      </DescriptionWrapper>
      {optionName && <OptionName>{optionName}</OptionName>}
      {optionValue && <OptionValue>{optionValue}</OptionValue>}
    </Component>
  );
}

export default OrderCard;

const Component = styled.div`
  border: 1px solid ${colors.gray100};
  margin: 30px 0;
  padding: 2px 15px;
`;

const PictureWrapper = styled.div`
  padding: 5px 0;
`;

const DescriptionWrapper = styled.div`
  margin-top: 5px;
`;
const Name = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 24px;
`;
const PriceWrapper = styled.div`
  max-width: 460px;
  display: flex;
  align-items: center;

  & > div:not(:last-child) {
    margin-right: 15px;
  }
`;

const Price = styled.div<{ hasDiscount?: boolean; hasDiscountPLus?: boolean }>`
  ${({ hasDiscount, hasDiscountPLus }) =>
    hasDiscount && !hasDiscountPLus
      ? css`
          font-style: normal;
          line-height: 20px;
          text-decoration-line: line-through;
          font-weight: 500;
          font-size: 14px;
          color: ${colors.darkGrey};
        `
      : hasDiscount && hasDiscountPLus
      ? css`
          font-style: normal;
          line-height: 26px;
          font-weight: 600;
          font-size: 18px;
          text-decoration-line: line-through;
          color: ${colors.black};
        `
      : css`
          font-style: normal;
          line-height: 26px;
          font-weight: 600;
          font-size: 22px;
          color: ${colors.primary};
        `}
`;

const OptionName = styled.span`
  font-style: normal;
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;

  color: ${colors.darkGrey};
`;
const OptionValue = styled.span`
  margin-left: 20px;
  font-style: normal;
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
`;

const StyledPicture = styled(Picture)`
  height: 120px;
  width: 100%;
  picture,
  img {
    height: 100%;
    object-fit: contain;
    object-position: left;
  }
`;
